import { useCallback } from 'react';
import { apiGet } from '../../api/apiClient';
import {
    indexerTradeToMarketTrade,
    type IndexerTradeItem,
} from '../../api/sidebarTradeTypes';
import {
    DEFAULT_TRADE_TYPES,
    onlyFills,
    tradeTypeOf,
    type TradeTypeId,
    type WalletTrade,
} from './tradeTypes';
import { usePerWallet } from './usePerWallet';

type TradeItem = IndexerTradeItem & { trade_type?: string | null };

interface TradesResponse {
    trades: TradeItem[];
}

export interface WalletTradesResult {
    tradesByWallet: ReadonlyMap<string, WalletTrade[]>;
    loadingWallets: ReadonlySet<string>;
    failedWallets: ReadonlySet<string>;
    retryWallet: (wallet: string) => void;
}

const TRADE_LIMIT = 5000;
const REQUEST_TIMEOUT_MS = 45_000;
const START_MARGIN_MS = 7 * 24 * 60 * 60 * 1000;
const END_MARGIN_MS = 30 * 24 * 60 * 60 * 1000;

export function tradesFromTs(start: Date | null): string | null {
    if (!start || Number.isNaN(start.getTime())) return null;
    return new Date(start.getTime() - START_MARGIN_MS).toISOString();
}

export function tradesToTs(end: Date | null): string | null {
    if (!end || Number.isNaN(end.getTime())) return null;
    return new Date(end.getTime() + END_MARGIN_MS).toISOString();
}

export function useWalletTrades(
    eventId: string | null,
    marketId: string | null,
    wallets: readonly string[],
    start: Date | null = null,
    closedEnd: Date | null = null,
    types: readonly TradeTypeId[] = DEFAULT_TRADE_TYPES
): WalletTradesResult {
    const fromTs = tradesFromTs(start);
    const toTs = tradesToTs(closedEnd);
    const typesKey = types.join(',');
    const scope =
        eventId && marketId
            ? `${eventId}|${marketId}|${typesKey}|${fromTs ?? ''}|${toTs ?? ''}`
            : null;

    const load = useCallback(
        async (wallet: string, signal: AbortSignal): Promise<WalletTrade[]> => {
            const params = new URLSearchParams({
                markets: marketId ?? '',
                accounts: wallet,
                limit: String(TRADE_LIMIT),
            });
            if (fromTs) params.set('from_ts', fromTs);
            if (toTs) params.set('to_ts', toTs);
            const wanted = typesKey.split(',') as TradeTypeId[];
            if (!onlyFills(wanted)) params.set('trade_types', typesKey);
            const res = await apiGet<TradesResponse>(
                `/api/trades/event/${encodeURIComponent(eventId ?? '')}?${params}`,
                signal
            );
            return (res.trades ?? []).map(item => ({
                ...indexerTradeToMarketTrade(item),
                tradeType: tradeTypeOf(item.trade_type),
            }));
        },
        [eventId, marketId, fromTs, toTs, typesKey]
    );

    const result = usePerWallet(scope, wallets, load, REQUEST_TIMEOUT_MS);

    return {
        tradesByWallet: result.byWallet,
        loadingWallets: result.loadingWallets,
        failedWallets: result.failedWallets,
        retryWallet: result.retryWallet,
    };
}
